import { Input, Select } from '../ui/index.jsx'

type FieldType = 'boolean' | 'select' | 'date' | 'number' | 'text'

export interface CustomField {
  id: string
  label: string
  type: FieldType
  required?: boolean
  placeholder?: string
  options?: string[]
}

export type FieldAnswers = Record<string, string | boolean>

interface CustomFieldsRendererProps {
  fields: CustomField[] | undefined | null
  answers: FieldAnswers
  onChange: (answers: FieldAnswers) => void
  t: (key: string) => string
}

export default function CustomFieldsRenderer({ fields, answers, onChange, t }: CustomFieldsRendererProps) {
  if (!fields || fields.length === 0) return null

  const set = (id: string, value: string | boolean) => onChange({ ...answers, [id]: value })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid var(--border-subtle)' }}>
      {fields.map(field => {
        const rawValue = answers[field.id] ?? ''
        const value = typeof rawValue === 'boolean' ? '' : rawValue
        const label = field.required ? `${field.label} *` : field.label

        if (field.type === 'boolean') {
          return (
            <label key={field.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 'var(--text-base)' }}>
              <input
                type="checkbox"
                checked={!!answers[field.id]}
                onChange={e => set(field.id, e.target.checked)}
                style={{ width: 18, height: 18, accentColor: 'var(--accent)' }}
              />
              <span>{field.label}</span>
              {field.required && <span style={{ color: 'var(--danger)' }}>*</span>}
            </label>
          )
        }

        if (field.type === 'select') {
          return (
            <Select key={field.id} label={label} value={value} onChange={e => set(field.id, e.target.value)}>
              <option value="">{field.placeholder || `— ${t('select')} —`}</option>
              {(field.options || []).map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </Select>
          )
        }

        if (field.type === 'date') {
          return (
            <Input key={field.id} label={label} type="date" value={value}
              onChange={e => set(field.id, e.target.value)} />
          )
        }

        if (field.type === 'number') {
          return (
            <Input key={field.id} label={label} type="number" value={value}
              onChange={e => set(field.id, e.target.value)}
              placeholder={field.placeholder || ''} />
          )
        }

        // Default: text
        return (
          <Input key={field.id} label={label} value={value}
            onChange={e => set(field.id, e.target.value)}
            placeholder={field.placeholder || ''} />
        )
      })}
    </div>
  )
}

/** Check if all required custom fields for a step have answers */
export function validateCustomFields(fields: CustomField[] | null | undefined, answers: FieldAnswers): boolean {
  if (!fields) return true
  return fields
    .filter(f => f.required)
    .every(f => {
      const val = answers[f.id]
      if (f.type === 'boolean') return true // boolean is always answered (false is valid)
      return val !== undefined && val !== null && val !== ''
    })
}
